import moment from 'moment'
import React from 'react'
import { TidewaysContext } from '../../app/context'
import Navigator from '../../app/navigator'
import { ActiveSlot } from '../../domain/ActiveSlot'
import { EventItem } from '../../domain/EventItem'
import { GraphEntity } from '../../domain/GraphEntity'
import { Operation } from '../../domain/Operation'
import { PercentileType } from '../../domain/ReportCriteria'
import BoxBody from '../../helper/boxBody'
import LargeNumber from '../../helper/large-number'
import Link from '../../helper/link'
import Memory from '../../helper/memory'
import Milliseconds from '../../helper/milliseconds'
import Percent from '../../helper/percent'
import Shorten from '../../helper/shorten'
import { OperationsGraphEntity } from '../../redux/operations/OperationsState'
import Chart from '../graph.d3'
import Snapshot from '../snapshot/snapshotModel'
import TimeRangeLabel from '../timeRangeLabel'
import Downstream from './downstream'
import { newResizeObserver } from '../../helper/resizeObserver'
import { convertDate } from '../../helper/convertDate'

interface Props {
    navigator: Navigator
    parameters: { [key: string]: string }
    tidewaysContext: TidewaysContext
    responseType: PercentileType
    loading: boolean
    data: GraphEntity | OperationsGraphEntity
    dataFixedTimezone?: string
    changeActiveSlot: (activeSlot: ActiveSlot) => void
    operation?: Operation
    children?: React.ReactNode
}

export class GraphComponent extends React.PureComponent<Props> {
    public static displayName = 'Graph'

    public static defaultProps = {
        responseType: '95p',
        loading: false,
        data: null,
    }

    public percentiles = {
        '50p': '50% Percentile',
        '90p': '90% Percentile',
        '95p': '95% Percentile',
        '99p': '99% Percentile',
        '99.9p': '99.9% Percentile',
        avg: 'Average',
        request: '95% Percentile',
        max: 'Maximum',
    }

    public nodes: { svg: SVGSVGElement | null } = { svg: null }
    public chart
    public chartDownstream
    public resizeObserver

    public componentDidMount() {
        if (!this.chart) {
            this.createChart()
        }
    }

    public componentDidUpdate(prevProps) {
        if (!this.chart) {
            this.createChart()
        }

        if (!this.resizeObserver && this.chart) {
            this.setResizeObserver()
        }

        if (this.props.data) {
            this.updateChart()
        }

        if (this.props.parameters.type || this.props.responseType) {
            this.setResponseTypeLabel()
        }
    }

    // destroy function is empty!
    public componentWillUnmount() {
        this.chart?.destroy()
        this.resizeObserver?.disconnect()
    }

    public getChartElement = () => this.nodes.svg

    public createChart() {
        this.chart = new Chart()

        this.setResponseTypeLabel()

        const chartElement = this.getChartElement()
        if (chartElement) {
            const parentNode = jQuery(chartElement).parent()

            this.chart.create(chartElement, parentNode.width(), 190, (timeIndex, timeIndexNext, mouse) =>
                this.showTooltip(timeIndex, timeIndexNext, mouse, 'main'),
            )
        }

        if (this.props.data) {
            this.updateChart()
        }
    }

    public updateChart = () => {
        const events = this.events()

        this.chart.update(
            [
                {
                    name: 'requests',
                    data: Object.keys(this.props.data.report.requests).map(key => {
                        const value = this.props.data.report.requests[key]

                        return {
                            axis: 'requests',
                            date: convertDate(key, this.props.dataFixedTimezone),
                            dateUtc: key,
                            value,
                            responseTime: this.props.data.report.percentileResponseTimes[key] || null,
                            requests: this.props.data.report.requests[key] || null,
                            downstream: this.props.data.report.downstream[key] || null,
                            errors: this.props.data.report.errors[key] || null,
                        }
                    }),
                },
                {
                    name: 'response',
                    data: Object.keys(this.props.data.report.percentileResponseTimes).map(key => {
                        const value = this.props.data.report.percentileResponseTimes[key]

                        return {
                            axis: 'response',
                            date: convertDate(key, this.props.dataFixedTimezone),
                            dateUtc: key,
                            downstream: this.props.data.report.downstream[key] || null,
                            value,
                        }
                    }),
                },
                {
                    name: 'errors',
                    data: Object.keys(this.props.data.report.errors).map(key => {
                        const value = this.props.data.report.errors[key]

                        return {
                            axis: 'requests',
                            date: convertDate(key, this.props.dataFixedTimezone),
                            dateUtc: key,
                            value,
                        }
                    }),
                },
            ],
            events.map(event => {
                if (!moment.isDate(event.eventDate)) {
                    event.eventDate = convertDate(event.eventDate, this.props.dataFixedTimezone)
                }

                return event
            }),
            this.props.parameters.end ? Snapshot.fromParameters(this.props.parameters).getRangeMarker() : [],
            this.props.data.report.hasDownstream,
            this.props.data.report.responseTimeTarget,
        )
    }

    public showTooltip(timestamp, timestampNext, mouse, source) {
        if (source === 'downstream' && this.chart && timestamp) {
            this.chart.highlight(
                convertDate(timestamp, this.props.dataFixedTimezone),
                convertDate(timestampNext, this.props.dataFixedTimezone),
            )
        }

        if (source === 'main' && this.chartDownstream && timestamp) {
            this.chartDownstream.highlight(
                convertDate(timestamp, this.props.dataFixedTimezone),
                convertDate(timestampNext, this.props.dataFixedTimezone),
            )
        }

        this.props.changeActiveSlot({
            timeIndex: timestamp,
            mouse,
        })
    }

    private setResponseTypeLabel = () => {
        const type = this.props.parameters.type || this.props.responseType

        this.chart.setResponseTypeLabel(this.percentiles[type], this.percentiles[type])
    }

    private rebuildChart = () => {
        this.destroyChart()
        this.createChart()
        if (this.chart) this.updateChart()
    }

    private destroyChart = () => {
        this.chart?.destroy() // remove svg-elements from dom
        this.chart = null // remove reference to the object
    }

    private setResizeObserver = () => {
        const chartElement = this.getChartElement()
        if (chartElement) {
            const parentNode = jQuery(chartElement).parent()
            this.resizeObserver = newResizeObserver(this.rebuildChart)
            this.resizeObserver.observe(parentNode[0])
        }
    }

    public render() {
        const events = this.events()

        const tidewaysContext = this.props.tidewaysContext

        const errorTransactionParams = this.props.operation ? { transactionIds: [this.props.operation.id] } : {}

        return (
            <div>
                <div className="box box-default">
                    <div className="box-header">
                        <nav className="navbar navbar-toolbar">
                            <div className="container-fluid">
                                {this.props.children ? (
                                    this.props.children
                                ) : (
                                    <h3 className="box-title">
                                        {this.props.operation ? (
                                            <Shorten string={this.props.operation.name} />
                                        ) : (
                                            <span>
                                                Response Times for <TimeRangeLabel />{' '}
                                                {this.props.tidewaysContext.isProductionEnvironment() ? null : (
                                                    <span className={'label label-default'} style={{ fontSize: '12px' }}>
                                                        <a
                                                            className={'tw-font-weight-normal text-white'}
                                                            target="_blank"
                                                            href="https://support.tideways.com/documentation/features/application-monitoring/environments.html?utm_medium=app&utm_source=graph&utm_campaign=limited-visibility#limited-visibility-environments"
                                                            rel="noreferrer"
                                                        >
                                                            Limited Visibility
                                                        </a>
                                                    </span>
                                                )}
                                            </span>
                                        )}
                                    </h3>
                                )}

                                <ul className="nav navbar-nav navbar-right navbar-numbers">
                                    <li>
                                        <a href="#" className="dropdown-toggle" data-toggle="dropdown">
                                            {this.props.data.report.responseTimeIncident ? (
                                                <Link
                                                    route="XhprofErrorBundle.Issues.incident"
                                                    parameters={{ error: this.props.data.report.responseTimeIncident }}
                                                    style={{ color: '#f56954' }}
                                                    title="View incident for transaction in this period"
                                                >
                                                    <span className="fa fa-warning"></span>{' '}
                                                </Link>
                                            ) : null}
                                            <Milliseconds value={this.props.data.report.slices.total.responseTime * 1000} />{' '}
                                            <small>{this.percentiles[this.props.parameters.type || this.props.responseType]}</small>{' '}
                                            <span className="fa fa-caret-down" />
                                        </a>

                                        <ul className="dropdown-menu" role="menu">
                                            {Object.keys(this.percentiles).map(key => {
                                                const name = this.percentiles[key]

                                                if (key === 'request') {
                                                    return null
                                                }

                                                return (
                                                    <li key={key}>
                                                        <a
                                                            onClick={() => {
                                                                this.props.navigator.replace({
                                                                    ...tidewaysContext.toParameters(),
                                                                    type: key,
                                                                })
                                                            }}
                                                        >
                                                            {name}
                                                        </a>
                                                    </li>
                                                )
                                            })}
                                        </ul>
                                    </li>
                                    {this.props.data.report.slices.total.memory && this.props.data.report.slices.total.memory > 0 ? (
                                        <li>
                                            <div style={{ padding: '10px', lineHeight: '20px' }}>
                                                <Memory value={this.props.data.report.slices.total.memory * 1024} />{' '}
                                                <small>Max Memory</small>
                                            </div>
                                        </li>
                                    ) : null}
                                    <li>
                                        <div style={{ padding: '10px', lineHeight: '20px' }}>
                                            <LargeNumber value={this.props.data.report.slices.total.requests} /> <small>Requests</small>
                                        </div>
                                    </li>
                                    {this.props.data.report.slices.total.pageCache.hits > 0 ? (
                                        <li>
                                            <a>
                                                <Percent
                                                    value={
                                                        this.props.data.report.slices.total.pageCache.hits /
                                                        (this.props.data.report.slices.total.pageCache.hits +
                                                            this.props.data.report.slices.total.pageCache.misses)
                                                    }
                                                />{' '}
                                                <small>Page Cache Hit Rate</small>
                                            </a>
                                        </li>
                                    ) : null}
                                    {this.props.tidewaysContext.hasFeature('crawler_ratio') &&
                                    this.props.data.report.slices.total.crawlers > 0 ? (
                                        <li>
                                            <a href="">
                                                <Percent
                                                    value={
                                                        this.props.data.report.slices.total.crawlers /
                                                        this.props.data.report.slices.total.requests
                                                    }
                                                />
                                                &nbsp;
                                                <small>Crawlers/Bots</small>
                                            </a>
                                        </li>
                                    ) : null}
                                    {events.length > 0 ? (
                                        <li>
                                            <Link
                                                title="Events"
                                                route="XhprofOrganizationBundle.Application.show"
                                                params={{
                                                    event: events[0] ? events[0].id : null,
                                                    events: events.map(event => event.id).join(','),
                                                }}
                                            >
                                                {events.length} <small>Events</small>
                                            </Link>
                                        </li>
                                    ) : this.props.tidewaysContext.application.license.events ? (
                                        <li>
                                            <Link title="Events" route="XhprofProfilerBundle.Event.create">
                                                0 <small>Events</small>
                                            </Link>
                                        </li>
                                    ) : null}
                                    {!this.props.data.report.errors ? null : (
                                        <li>
                                            <Link
                                                title="See errors causing these failures"
                                                route="XhprofErrorBundle.Issues.errors"
                                                params={errorTransactionParams}
                                            >
                                                {this.props.data.report.failureRateIncident ? (
                                                    <Link
                                                        route="XhprofErrorBundle.Issues.incident"
                                                        parameters={{ error: this.props.data.report.failureRateIncident }}
                                                        style={{ color: '#f56954' }}
                                                        title="View incident for transaction in this period"
                                                    >
                                                        <span className="fa fa-warning"></span>{' '}
                                                    </Link>
                                                ) : null}
                                                <Percent value={this.props.data.report.slices.total.errorRate / 100} class="error" />{' '}
                                                <small>Failures</small>
                                            </Link>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </nav>
                    </div>

                    <BoxBody {...this.props}>
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="chart">
                                    <svg
                                        ref={element => {
                                            this.nodes.svg = element
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        {this.props.data.report.hasDownstream ? (
                            <Downstream
                                {...this.props}
                                onTimestampSelected={(timestamp, timestampNext, mouse) =>
                                    this.showTooltip(timestamp, timestampNext, mouse, 'downstream')
                                }
                                onChartCreated={chart => {
                                    this.chartDownstream = chart
                                }}
                            />
                        ) : null}
                    </BoxBody>
                </div>
            </div>
        )
    }

    private events = (): EventItem[] => {
        const { events = [] } = this.props.data as GraphEntity

        return events === null ? [] : events
    }
}

export default GraphComponent
